import React from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import TemporaryIcon from 'components/global/TemporaryIcon'
import { CustomTooltipContainer, CTTitle, CTLine, CTLeftContent, CTColor, CTLabel, CTValue, CTTotalSection } from '../components/TooltipStyles'
import { formatBytesWithUnit } from '../../../helpers'

interface EvolutionTooltipProps {
  active?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any[]
  isActiveUsers?: boolean
}

const EvolutionTooltip: React.FC<EvolutionTooltipProps> = ({ active, payload, isActiveUsers = false }) => {
  const { t, i18n } = useTranslation()
  
  if (!active || !payload?.length) return null

  const data = payload[0]?.payload
  if (!data) return null

  const monthName = moment(data.date).format('MMMM YYYY')
  const growthValue = Math.round(parseFloat(data.percentage) || 0)
  const isPositive = growthValue >= 0
  const hasValidData = data.hasCurrentData && data.hasPastData

  return (
    <CustomTooltipContainer sx={{ width: '280px' }}>
      <CTTitle>
        {monthName.toUpperCase()}
      </CTTitle>
      
      {payload.map((entry, index) => {
        const isCurrentYear = entry.dataKey.includes('current_year') || entry.dataKey.includes('count_current_year')
        const year = isCurrentYear ? moment(data.date).year() : moment(data.date).year() - 1
        const value = entry.value || 0
        const color = entry.color
        const hasData = isCurrentYear ? data.hasCurrentData : data.hasPastData
        
        let formattedValue = '-'
        let unit = ''
        
        if (hasData && value > 0) {
          if (isActiveUsers) {
            formattedValue = value.toString().replace(/,/g, ' ')
            unit = t('stats.legends.devices')
          } else {
            const formatted = formatBytesWithUnit(value, i18n.language)
            formattedValue = formatted.value
            unit = formatted.unit
          }
        }
        
        return (
          <CTLine key={index}>
            <CTLeftContent>
              <CTColor color={color} />
              <CTLabel>
                {year}
              </CTLabel>
            </CTLeftContent>
            
            <Box sx={{ 
              flex: 1,
              height: '1px', 
              backgroundColor: 'white', 
              mx: 1
            }} />
            
            <CTValue>
              {hasData && value > 0 ? (
                <>
                  {formattedValue}
                  {unit && (
                    <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                      {unit}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: '12px', color: '#989898' }}>-</Typography>
              )}
            </CTValue>
          </CTLine>
        )
      })}
      
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
      }}>
        <CTTotalSection sx={{ 
          justifyContent: 'center',
          display: 'inline-flex',
          pl: 4,
        }}>
          <Typography sx={{ 
            fontSize: '10px', 
            color: '#666666', 
          }}>
            {t('stats.evolution.trend')}
          </Typography>
          {hasValidData ? (
            <Typography sx={{ 
              fontSize: '10px', 
              color: isPositive ? '#587F31' : '#DC2626', 
              display: 'flex',
              alignItems: 'center',
              gap: 0.25
            }}>
              {isPositive ? '+' : ''}{growthValue}%
              <TemporaryIcon 
                iconlabel={isPositive ? 'arrow-trend-up' : 'arrow-trend-down'}
                iconsize="10px" 
              />
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '10px', color: '#989898' }}>-</Typography>
          )}
        </CTTotalSection>
      </Box>
    </CustomTooltipContainer>
  )
}

export default EvolutionTooltip